import React, { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { get } from 'lodash';
import { FormGroup, Box } from '@mui/material';
import { FormDataType } from 'contexts/FormProvider';
import { Accordion, Checkbox, useTheme } from '@qbe/ads-core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';
import { dataPrivacyList } from 'resources/dataPrivacy';

import { transformedCheckboxValue } from 'utils/utils';
import { SEPARATOR } from 'utils/constants';

import { TextFieldController } from 'controllers';

type Props = {
  form: UseFormReturn<FormDataType>;
};

const DataPrivacy: FC<Props> = ({ form }) => {
  const { watch, control } = form;

  const otherDetails = watch('otherDetails', '');

  const selected = transformedCheckboxValue(watch('dataPrivacy'));

  const getHeaderIcon = (heading: string) => {
    const haveValue = get(selected, heading, []).length > 0;
    return haveValue
      ? {
          muiIcon: CheckCircleIcon,
        }
      : {};
  };

  const {
    color: {
      accent: { primary: iconColor },
    },
  } = useTheme() as any;

  const { isTablet, isDesktop } = useBreakpoint();
  return (
    <>
      <Controller
        control={control}
        name="dataPrivacy"
        render={({ field }) => (
          <>
            {dataPrivacyList.map((data, index) => (
              <Accordion
                key={index}
                id={'' + index}
                title={data.heading}
                iconProps={getHeaderIcon(data.heading)}
                sx={{
                  width: '100%',
                  maxWidth: '450px',
                  '.MuiAccordionSummary-content .MuiSvgIcon-root': {
                    color: iconColor,
                  },
                }}
                variant="compressed"
                body={
                  (
                    <FormGroup>
                      {data.categories.map((category, index) => (
                        <Box marginTop={index > 0 ? 2 : 1} key={category.id}>
                          <Checkbox
                            key={category.id}
                            name={field.name}
                            // todo: when this is fixed can remove this workaround
                            // https://qbe-appservices.atlassian.net/browse/AD1-581
                            sx={{
                              alignItems: `${isTablet ? 'center' : 'start'}`,
                              '.MuiCheckbox-root': {
                                marginTop: '2.5px',
                              },
                              '.css-cyfb05-MuiTypography-root': {
                                fontSize: `${isTablet ? '12px' : '1rem'}`,
                              },
                              '.css-1w2yy45-MuiTypography-root': {
                                fontSize: `${isTablet ? '9px' : '0.70rem'}`,
                              },
                              width: `${category.text.length > 35 ? '120%' : '100%'}`,
                            }}
                            checked={field.value?.some(
                              (existingValue) => existingValue === `${data.heading}${SEPARATOR}${category.text}`
                            )}
                            onChange={(event, checked) => {
                              if (checked) {
                                field.onChange([...(field.value ?? []), (event.target as any).value]);
                              } else {
                                field.onChange(field.value?.filter((value) => value !== (event.target as any).value));
                              }
                            }}
                            size="large"
                            id={`${data.heading}${SEPARATOR}${category.text}`}
                            label={category.text}
                            value={`${data.heading}${SEPARATOR}${category.text}`}
                            data-track="dataPrivacy"
                            data-track-value="mask"
                            subText={category.info}
                          />
                        </Box>
                      ))}
                    </FormGroup>
                  ) as unknown as string
                }
              ></Accordion>
            ))}
          </>
        )}
      />

      <Accordion
        sx={{
          width: '100%',
          maxWidth: '450px',
          '.MuiAccordionSummary-content .MuiSvgIcon-root': {
            color: iconColor,
          },
        }}
        variant="compressed"
        title="Other"
        iconProps={
          otherDetails
            ? {
                muiIcon: CheckCircleIcon,
              }
            : {}
        }
        id="Other"
        body={
          (
            <TextFieldController
              name="otherDetails"
              control={control}
              label="Please provide details"
              inputProps={{
                maxLength: 50,
                minLength: 1,
              }}
            />
          ) as any
        }
      ></Accordion>
    </>
  );
};

export default DataPrivacy;
